import { handleApiResponse, queryKeys } from "@stockpilot/shared";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import {
  desc,
  type Organization,
  organization,
  type PowerSyncDrizzleDB,
} from "@stockpilot/powersync";
import { type Accessor, createMemo } from "solid-js";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { activeScopeIdFor, setActiveScopeIdFor } from "@/features/organizations/active-org";

/** Entrada del switcher: una org real o el workspace Personal (id = userId). */
export interface OrgScope {
  /** Id de scope: id de org real, o el userId (Personal). Es el `organization_id`. */
  id: string;
  /** Nombre a mostrar. */
  name: string;
  /** `true` para el workspace Personal (no es una organización real). */
  personal: boolean;
}

export interface ActiveOrgContext {
  /** Organizaciones reales del usuario, sincronizadas en local (miembro). */
  orgs: Accessor<Organization[]>;
  /** Entradas del switcher: [Personal, ...orgs]. */
  scopes: Accessor<OrgScope[]>;
  /**
   * `organization_id` ACTIVO — la fuente única para el scope del WMS. Es el id
   * de la org activa, o el userId cuando el scope activo es Personal. Validado
   * contra lo que existe en local (auto-sanación: cae a Personal si el puntero
   * quedó viejo). Todas las queries del WMS derivan de aquí, así que un switch
   * re-scopea reactivamente catálogo/almacenes/existencias/operaciones/reportes.
   */
  activeScopeId: Accessor<string>;
  /** Organización activa, o `null` si el scope activo es Personal. */
  activeOrg: Accessor<Organization | null>;
  /** Scope activo (org o Personal) como entrada de switcher. */
  activeScope: Accessor<OrgScope>;
  /** Cambia el scope activo (persiste por usuario; re-scopea todo el WMS). */
  setActiveOrg: (scopeId: string) => void;
  /**
   * Crea una organización (org+member atómica server-side, CC-3), espera a que
   * sincronice a local, la deja como ACTIVA y devuelve su id. Es la vía de alta
   * tanto del PRIMER supermercado/empresa (onboarding) como de los adicionales.
   */
  bootstrapOrg: (name: string) => Promise<string>;
}

/**
 * Contexto multi-organización del WMS: la empresa/supermercado activo y el
 * conjunto de empresas del usuario.
 *
 * Modelo org-céntrico: cada organización es una empresa; dentro viven sus
 * almacenes y ubicaciones (entidades `warehouse`/`location` con
 * `organization_id`). Un usuario puede ser miembro de N orgs (sync stream
 * `organizations` baja `organization`/`member`); el inventario de cada org baja
 * por `inventory_org` (`organization_id IN my_org_ids`). El workspace Personal
 * (F1) es el inventario con `organization_id = user_id`, que baja por
 * `inventory_personal` — se ofrece como scope por defecto para no romper los
 * datos previos ni el modo visitante.
 *
 * El scope activo es una preferencia persistida por usuario (`active-org.ts`)
 * validada contra las orgs en local; todas las queries derivan de
 * `activeScopeId()`, así que un switch re-scopea el WMS completo de forma
 * reactiva.
 */
export function useActiveOrg(): ActiveOrgContext {
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();
  const session = useSession();

  const orgsQuery = createPowerSyncQuery<Organization>({
    queryKey: () => queryKeys.inventory.memberships(),
    queryFn: () => {
      const d = drizzle();
      if (!d) {
        return null;
      }
      return d.select().from(organization).orderBy(desc(organization.created_at));
    },
    db: () => db(),
  });

  const orgs = createMemo<Organization[]>(() => orgsQuery.data ?? []);

  const scopes = createMemo<OrgScope[]>(() => [
    { id: session.userId(), name: "Personal", personal: true },
    ...orgs().map((o) => ({ id: o.id, name: o.name ?? o.slug ?? o.id, personal: false })),
  ]);

  const activeScopeId = createMemo<string>(() => {
    const uid = session.userId();
    // Preferencia persistida validada contra lo que existe en local. Personal
    // (scope === userId) siempre es válido; si el puntero apunta a una org que
    // ya no está en local (borrada / no sincronizada / dato viejo) caemos a
    // Personal — nunca bloquea (auto-sanación).
    const preferred = activeScopeIdFor(uid);
    if (!preferred || preferred === uid) {
      return uid;
    }
    return orgs().some((o) => o.id === preferred) ? preferred : uid;
  });

  const activeOrg = createMemo<Organization | null>(
    () => orgs().find((o) => o.id === activeScopeId()) ?? null,
  );

  const activeScope = createMemo<OrgScope>(() => {
    const org = activeOrg();
    if (org) {
      return { id: org.id, name: org.name ?? org.slug ?? org.id, personal: false };
    }
    return { id: session.userId(), name: "Personal", personal: true };
  });

  const setActiveOrg = (scopeId: string): void => {
    setActiveScopeIdFor(session.userId(), scopeId);
  };

  async function bootstrapOrg(name: string): Promise<string> {
    const d = drizzle();
    if (!d) {
      throw new Error("Local database is not ready");
    }
    // Ya puede haber orgs en local: capturamos las ids previas para esperar a la
    // org NUEVA (no a "cualquier org", que resolvería al instante con otra).
    const before = new Set(orgs().map((existing) => existing.id));
    const trimmed = name.trim();
    const slug =
      trimmed
        .toLowerCase()
        .replaceAll(/[^a-z0-9]+/g, "-")
        .replaceAll(/^-+|-+$/g, "")
        .slice(0, 40) || `org-${Date.now()}`;
    // `openapi-fetch` NO lanza ante 4xx/5xx (devuelve `{error}`): sin este
    // wrapper un 401 (visitante sin cuenta al crear org) moría en silencio y el
    // poll de abajo esperaría 30s a una org que jamás llega — el botón "Create"
    // se quedaría pegado sin feedback (CC-30 / familia CC-8). `handleApiResponse`
    // lanza en no-2xx y el switcher pinta el error inline.
    await handleApiResponse(
      api.POST("/api/org/createOrganization", {
        body: { name: trimmed, slug },
      }),
    );
    // La respuesta solo confirma `success`; el id llega cuando la org sincroniza
    // a local (server-side crea org+member atómico, CC-3).
    const orgId = await waitForNewOrgLocal(d, before);
    if (!orgId) {
      throw new Error("Organization did not sync to local in time");
    }
    // La recién creada pasa a ser la activa (onboarding: era la única; alta
    // adicional: el usuario acaba de pedirlo — el switch es la expectativa).
    setActiveOrg(orgId);
    return orgId;
  }

  return {
    orgs,
    scopes,
    activeScopeId,
    activeOrg,
    activeScope,
    setActiveOrg,
    bootstrapOrg,
  };
}

const POLL_INTERVAL_MS = 200;
const MAX_WAIT_MS = 30_000;

/**
 * Espera (con polling local) a que aparezca una organización que NO estaba en
 * `before` y devuelve su id (la más reciente de las nuevas).
 */
async function waitForNewOrgLocal(
  d: PowerSyncDrizzleDB,
  before: ReadonlySet<string>,
): Promise<string | null> {
  const start = Date.now();
  while (Date.now() - start < MAX_WAIT_MS) {
    const rows = await d.select().from(organization).orderBy(desc(organization.created_at));
    const fresh = rows.find((row) => !before.has(row.id));
    if (fresh?.id) {
      return fresh.id;
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
  return null;
}
